import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { logout as logoutApi } from '../api/auth';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated } from '../api/auth';

const IDLE_TIMEOUT = 30 * 60 * 1000; // 30분
const WARNING_BEFORE = 60 * 1000; // 1분 전 경고

const IdleTimeoutContext = createContext(null);

export function IdleTimeoutProvider({ children }) {
  const navigate = useNavigate();
  const [loggedIn, setLoggedIn] = useState(isAuthenticated());
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(IDLE_TIMEOUT / 1000);

  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const countdownRef = useRef(null);
  const timerDisplayRef = useRef(null);

  // 로그인 상태 변경 감지
  useEffect(() => {
    const checkAuth = () => setLoggedIn(isAuthenticated());
    window.addEventListener('storage', checkAuth);
    const interval = setInterval(checkAuth, 1000);
    return () => {
      window.removeEventListener('storage', checkAuth);
      clearInterval(interval);
    };
  }, []);

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (timerDisplayRef.current) clearInterval(timerDisplayRef.current);
  }, []);

  const handleLogout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    logoutApi();
    setLoggedIn(false);
    navigate('/login');
  }, [clearAllTimers, navigate]);

  const resetTimer = useCallback(() => {
    if (!loggedIn) return;

    clearAllTimers();
    setShowWarning(false);
    setRemainingSeconds(IDLE_TIMEOUT / 1000);

    // 남은 시간 표시 업데이트 (1초마다)
    timerDisplayRef.current = setInterval(() => {
      setRemainingSeconds(prev => {
        if (prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);

    // 경고 표시 타이머 (29분 후)
    warningRef.current = setTimeout(() => {
      setShowWarning(true);
    }, IDLE_TIMEOUT - WARNING_BEFORE);

    // 로그아웃 타이머 (30분 후)
    timeoutRef.current = setTimeout(() => {
      handleLogout();
    }, IDLE_TIMEOUT);
  }, [loggedIn, clearAllTimers, handleLogout]);

  const extendSession = useCallback(() => {
    setShowWarning(false);
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!loggedIn) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    // 사용자 활동 이벤트
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      if (!showWarning) {
        resetTimer();
      }
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // 초기 타이머 시작
    resetTimer();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      clearAllTimers();
    };
  }, [loggedIn, showWarning, resetTimer, clearAllTimers]);

  // 분:초 포맷
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <IdleTimeoutContext.Provider value={{
      showWarning,
      remainingSeconds,
      remainingTime: formatTime(remainingSeconds),
      extendSession,
      logout: handleLogout,
      isLoggedIn: loggedIn
    }}>
      {children}
    </IdleTimeoutContext.Provider>
  );
}

export function useIdleTimeoutContext() {
  const context = useContext(IdleTimeoutContext);
  if (!context) {
    throw new Error('useIdleTimeoutContext must be used within IdleTimeoutProvider');
  }
  return context;
}
